import { InviteData, DriverInviteForm } from '../types/user';
import { TEAM_REMOVAL_HTTP } from '../constants/accountCancellationWorkflow';
import { getApiUrl } from '../utils/backendUrl';
import { REGISTRATION_TURNSTILE_TOKEN_FIELD } from '../constants/registrationAbuse';
import Logger from './logger';

// Get API URL dynamically - don't cache at module level to ensure correct environment
const getApiUrlValue = () => getApiUrl();

/** Canonical path for team member invite (backend also accepts invite_team_member as alias). */
const INVITE_TEAM_MEMBER_PATH = '/auth/invite-team-member';

/** Phase 2: Transfer ownership — only owner can call; backend path. */
const TRANSFER_OWNERSHIP_PATH = '/auth/transfer-ownership';
const COMPANY_MEMBERS_PATH = '/auth/company-members';

/** Phase 3: Claim ownership — public (no auth); company rep who hit "already registered" creates account and becomes owner. */
const REQUEST_OWNERSHIP_PATH = '/auth/request-ownership';

/** Phase 7: request team removal — email confirmation link to acting admin. */
const REQUEST_TEAM_REMOVAL_PATH = TEAM_REMOVAL_HTTP.REQUEST_PATH;

/** Phase 3 Owner Team Directory: pending invites and revoke (owner only). */
const PENDING_INVITES_PATH = '/auth/pending-invites';
const REVOKE_INVITE_PATH = '/auth/revoke-invite';

export interface TeamMemberInvitePayload {
  name: string;
  email: string;
  role: string;
  phone?: string;
}

export const inviteService = {
  /**
   * Invite a team member (broker/shipper/carrier). Sends email with registration link.
   * Requires Firebase ID token; backend resolves company from inviter's companyUsers.
   */
  async inviteTeamMember(
    idToken: string,
    payload: TeamMemberInvitePayload
  ): Promise<{ success: boolean; inviteLink?: string; error?: string }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${INVITE_TEAM_MEMBER_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          name: payload.name.trim(),
          email: payload.email.trim().toLowerCase(),
          role: payload.role.trim(),
          ...(payload.phone ? { phone: String(payload.phone).trim() } : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.message || data?.error || 'Failed to send invitation';
        return { success: false, error: message };
      }
      return { success: true, inviteLink: data.inviteLink };
    } catch (error: unknown) {
      Logger.error('Error sending team invite', error instanceof Error ? error : new Error(String(error)), { error });
      return { success: false, error: 'Failed to send invitation' };
    }
  },

  // Send driver invitation (legacy; no auth header)
  async sendDriverInvite(inviteData: DriverInviteForm, companyName: string, companyRep: string, inviterId: string): Promise<{ success: boolean; inviteLink?: string; error?: string }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}/auth/invite-driver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: inviteData.name,
          phone: inviteData.phoneNumber,
          email: inviteData.email,
          companyName,
          companyRep,
          inviterId
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send invitation');
      }

      const data = await response.json();
      return { success: true, inviteLink: data.inviteLink };
    } catch (error: any) {
      Logger.error('Error sending driver invite:', error as Error);
      return { success: false, error: error.message || 'Failed to send invitation' };
    }
  },

  // Fetch invite data by token
  async getInviteData(token: string): Promise<InviteData | null> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}/auth/invite/${token}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch invite data');
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      Logger.error('Error fetching invite data:', error as Error);
      return null;
    }
  },

  /**
   * Phase 2: Transfer company ownership to another member (owner only).
   * Provide newOwnerEmail or newOwnerUserId; backend validates same company.
   */
  async transferOwnership(
    idToken: string,
    payload: { newOwnerEmail?: string; newOwnerUserId?: string }
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${TRANSFER_OWNERSHIP_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          ...(payload.newOwnerEmail ? { newOwnerEmail: payload.newOwnerEmail.trim().toLowerCase() } : {}),
          ...(payload.newOwnerUserId ? { newOwnerUserId: String(payload.newOwnerUserId).trim() } : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = data?.message || data?.error || 'Failed to transfer ownership';
        return { success: false, error: message };
      }
      return { success: true };
    } catch (error: unknown) {
      Logger.error('Error transferring ownership', error instanceof Error ? error : new Error(String(error)), { error });
      return { success: false, error: 'Failed to transfer ownership' };
    }
  },

  /**
   * Phase 3: Request ownership when MC/DOT is already registered. Creates account, adds user to company, auto-approves (claimant becomes owner).
   * No auth required (caller does not have an account yet).
   */
  async requestOwnership(payload: {
    email: string;
    password: string;
    companyName: string;
    companyRep: string;
    phoneNumber?: string;
    mcNumber?: string;
    dotNumber?: string;
    userType: 'broker' | 'carrier';
    turnstileToken?: string;
  }): Promise<{ success: boolean; autoApproved?: boolean; error?: string }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${REQUEST_OWNERSHIP_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: payload.email.trim().toLowerCase(),
          password: payload.password,
          companyName: payload.companyName.trim(),
          companyRep: payload.companyRep.trim(),
          ...(payload.phoneNumber != null ? { phoneNumber: String(payload.phoneNumber).trim() } : {}),
          ...(payload.mcNumber != null ? { mcNumber: String(payload.mcNumber).trim() } : {}),
          ...(payload.dotNumber != null ? { dotNumber: String(payload.dotNumber).trim() } : {}),
          userType: payload.userType,
          ...(payload.turnstileToken
            ? { [REGISTRATION_TURNSTILE_TOKEN_FIELD]: payload.turnstileToken }
            : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data?.message || data?.error || data?.reason || 'Failed to request ownership.',
        };
      }
      return { success: true, autoApproved: data.autoApproved };
    } catch (error: unknown) {
      Logger.error('Error requesting ownership', error instanceof Error ? error : new Error(String(error)), { error });
      return { success: false, error: 'Failed to request ownership.' };
    }
  },

  /**
   * Phase 2: List company members (owner only). Used for transfer-ownership dropdown.
   */
  async getCompanyMembers(idToken: string): Promise<{ success: boolean; members?: Array<{ userId: string; email: string; name: string; role: string; companyUserRole: string }>; error?: string }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${COMPANY_MEMBERS_PATH}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, error: data?.message || data?.error || 'Failed to load members' };
      }
      return { success: true, members: data.members ?? [] };
    } catch (error: unknown) {
      Logger.error('Error fetching company members', error instanceof Error ? error : new Error(String(error)), { error });
      return { success: false, error: 'Failed to load members' };
    }
  },

  /**
   * Phase 7 Owner Team Directory: request removal — sends one-time link to your email; removal completes when you open the link.
   */
  async requestTeamMemberRemoval(
    idToken: string,
    payload: { userId: string }
  ): Promise<{ success: boolean; error?: string; confirmationPending?: boolean }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${REQUEST_TEAM_REMOVAL_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ userId: payload.userId.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: (data?.message || data?.error) as string || 'Failed to request removal',
        };
      }
      return { success: true, confirmationPending: data.confirmationPending === true };
    } catch (error: unknown) {
      Logger.error(
        'Error requesting team member removal',
        error instanceof Error ? error : new Error(String(error)),
        { error }
      );
      return { success: false, error: 'Failed to request removal' };
    }
  },

  /**
   * Phase 3 Owner Team Directory: list pending invites (owner only).
   */
  async getPendingInvites(idToken: string): Promise<{
    success: boolean;
    invites?: Array<{ token: string; email: string; name: string; role: string; createdAt?: string; expiresAt?: string }>;
    error?: string;
  }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${PENDING_INVITES_PATH}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, error: (data?.message || data?.error) as string || 'Failed to load pending invites' };
      }
      return { success: true, invites: data.invites ?? [] };
    } catch (error: unknown) {
      Logger.error('Error fetching pending invites', error instanceof Error ? error : new Error(String(error)), { error });
      return { success: false, error: 'Failed to load pending invites' };
    }
  },

  /**
   * Phase 3 Owner Team Directory: revoke an invite (owner only).
   */
  async revokeInvite(idToken: string, payload: { token: string }): Promise<{ success: boolean; error?: string }> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}${REVOKE_INVITE_PATH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ token: payload.token.trim() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return { success: false, error: (data?.message || data?.error) as string || 'Failed to revoke invite' };
      }
      return { success: true };
    } catch (error: unknown) {
      Logger.error('Error revoking invite', error instanceof Error ? error : new Error(String(error)), { error });
      return { success: false, error: 'Failed to revoke invite' };
    }
  },

  // Mark invite as used
  async markInviteAsUsed(token: string): Promise<boolean> {
    try {
      const API_URL = getApiUrlValue();
      const response = await fetch(`${API_URL}/auth/invite/${token}/use`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to mark invite as used');
      }

      return true;
    } catch (error: any) {
      Logger.error('Error marking invite as used:', error as Error);
      return false;
    }
  }
}; 